// client/src/lib/attribution.js
// First-party UTM + click-ID attribution.
// No external deps. Call initAttribution() once at app boot; call getAttribution() anywhere.

import { isClientArea } from './client-area';

const STORAGE_KEY_FIRST = 'monaco_attr_first';
const STORAGE_KEY_LAST  = 'monaco_attr_last';
const COOKIE_NAME_FIRST = 'mfta';   // monaco first-touch attribution
const COOKIE_NAME_LAST  = 'mlta';   // monaco last-touch attribution
const COOKIE_DAYS       = 90;
const ANON_ID_KEY       = 'monaco_anon_id';

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

function getOrCreateAnonId() {
  try {
    var id = localStorage.getItem(ANON_ID_KEY);
    if (!id) { id = uuid(); localStorage.setItem(ANON_ID_KEY, id); }
    return id;
  } catch (e) {
    return uuid();
  }
}

function getParam(name) {
  try {
    return new URLSearchParams(window.location.search).get(name) || null;
  } catch (e) { return null; }
}

function getCookie(name) {
  try {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (e) { return null; }
}

function setCookie(name, value, days) {
  try {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + expires + ';path=/;SameSite=Lax';
  } catch (e) { /* swallow */ }
}

function truncate(val, max) {
  if (!val) return val;
  return val.length > max ? val.substring(0, max) : val;
}

function readCurrentTouch() {
  return {
    utmSource:   truncate(getParam('utm_source'), 255),
    utmMedium:   truncate(getParam('utm_medium'), 255),
    utmCampaign: truncate(getParam('utm_campaign'), 255),
    utmContent:  truncate(getParam('utm_content'), 255),
    utmTerm:     truncate(getParam('utm_term'), 255),
    utmId:       truncate(getParam('utm_id'), 255),       // Meta {{campaign.id}}
    fbclid:      truncate(getParam('fbclid'), 255),
    liFatId:     truncate(getParam('li_fat_id') || getCookie('li_fat_id') || null, 255),
    referrer:    document.referrer || null,
    path:        window.location.pathname,
    ts:          new Date().toISOString(),
  };
}

function hasUtmOrClickId(touch) {
  return !!(touch.utmSource || touch.fbclid || touch.liFatId);
}

function persist(touch) {
  try {
    var json = JSON.stringify(touch);
    // First touch: only write once
    if (!localStorage.getItem(STORAGE_KEY_FIRST)) {
      localStorage.setItem(STORAGE_KEY_FIRST, json);
      setCookie(COOKIE_NAME_FIRST, json, COOKIE_DAYS);
    }
    // Last touch: always overwrite when there is a UTM or click ID signal
    if (hasUtmOrClickId(touch)) {
      localStorage.setItem(STORAGE_KEY_LAST, json);
      setCookie(COOKIE_NAME_LAST, json, COOKIE_DAYS);
    }
  } catch (e) { /* swallow localStorage errors (private browsing quota) */ }
}

function load(key, cookieName) {
  try {
    var raw = localStorage.getItem(key) || getCookie(cookieName);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

/** Call once at React app root (App.js or index.js) */
export function initAttribution() {
  if (isClientArea()) return;
  getOrCreateAnonId();
  var touch = readCurrentTouch();
  persist(touch);
}

/**
 * Call on SPA route changes to update last-touch when a route
 * carries fresh UTMs (e.g. /call?utm_source=meta after ad click).
 */
export function trackRouteChange() {
  if (isClientArea()) return;
  var touch = readCurrentTouch();
  if (hasUtmOrClickId(touch)) persist(touch);
}

/** Returns { firstTouch, lastTouch, anonymousId } */
export function getAttribution() {
  if (isClientArea()) return { anonymousId: null, firstTouch: null, lastTouch: null };
  return {
    anonymousId: getOrCreateAnonId(),
    firstTouch:  load(STORAGE_KEY_FIRST, COOKIE_NAME_FIRST),
    lastTouch:   load(STORAGE_KEY_LAST,  COOKIE_NAME_LAST),
  };
}

/** Returns utm object shaped for react-calendly InlineWidget utm prop */
export function getCalendlyUtm() {
  var attr = getAttribution();
  var lastTouch = attr.lastTouch;
  if (!lastTouch) return {};
  return {
    utmSource:   lastTouch.utmSource   || undefined,
    utmMedium:   lastTouch.utmMedium   || undefined,
    utmCampaign: lastTouch.utmCampaign || undefined,
    utmContent:  lastTouch.utmContent  || undefined,
    utmTerm:     lastTouch.utmTerm     || undefined,
  };
}
